/**
 * Fetch Saved Content Page UseCase
 *
 * Pages through the locally-stored saved-content (Watch later) records,
 * fetches the matching media documents from Firestore, applies blocked /
 * hidden / usersBlockedMe filtering, and preserves the original path order.
 *
 * Mirrors `fetchLikedVideosPageUseCase` 1:1 — the only difference is the
 * local-list element type (`SavedContentStorage` instead of
 * `UserMediaLikeStorage`). The hidden-content filter is applied upstream
 * by `savedContentService.listLocalSavedContent` (Task 1), so this use
 * case only handles pagination, the Firestore `in` fetch, the
 * post-query guards, and re-ordering.
 *
 * Flutter parity:
 *  - lib/presentation/screens/content/saved_content_screen.dart:115-157
 *    (pagination shape, re-ordering against `valuesToSearch`).
 *  - lib/data/data_sources/content_data_source.dart:301-339
 *    (post-query filters: blocked, usersBlockedMe, hiddenContent — same
 *    as getLikedContent which `userMediaService.getMediasByPaths`
 *    already implements).
 *
 * `hasMore` semantics: Flutter uses
 * `_medias.isNotEmpty && _medias.length < _savedContentItems.length`
 * (`saved_content_screen.dart:152-153`). For parity with the already
 * shipped `fetchLikedVideosPageUseCase`, this use case uses the
 * equivalent `end < localItems.length` formulation: when the slice
 * returns at least one survivor `end` reaches `localItems.length`
 * exactly when `_medias.length` does. The two are equivalent in the
 * typical flow.
 */

import { userMediaService } from '@/domain/services/userMedia/userMediaService';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { SavedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface FetchSavedContentPageParams {
  localItems: SavedContentStorage[];
  pageIndex: number;
  pageSize?: number;
  currentUser: UserEntity | null;
}

export interface FetchSavedContentPageResult {
  medias: UserMediaEntity[];
  hasMore: boolean;
}

export class FetchSavedContentPageUseCase {
  /**
   * Execute one page fetch.
   *
   * Steps (mirroring Flutter `saved_content_screen.dart:_getData`):
   *  1. Slice `localItems` by [pageIndex * pageSize, pageIndex * pageSize + pageSize).
   *  2. Fetch the matching media documents (Firestore `in` query — order is not
   *     guaranteed by Firestore).
   *  3. Filter out blocked/hidden/usersBlockedMe medias (parity with
   *     content_data_source.dart:319-337 — same post-query guards as
   *     getLikedContent).
   *  4. Re-order the surviving medias by iterating `pathsInSlice` outer, then
   *     matching `media.relativePath === path` inner — required for parity
   *     with saved_content_screen.dart:144-150, since Firestore `in`
   *     queries don't honour input order.
   *  5. Compute `hasMore` from the slice bounds against the local list length.
   *
   * Errors from the service are not caught here — the hook layer is
   * responsible for logging via appLogger.error.
   */
  async execute(
    params: FetchSavedContentPageParams,
  ): Promise<FetchSavedContentPageResult> {
    const {
      localItems,
      pageIndex,
      pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
      currentUser,
    } = params;

    const start = pageIndex * pageSize;
    if (start >= localItems.length) {
      return { medias: [], hasMore: false };
    }

    const end = Math.min(start + pageSize, localItems.length);
    const pathsInSlice = localItems.slice(start, end).map((item) => item.path);

    if (pathsInSlice.length === 0) {
      return { medias: [], hasMore: false };
    }

    const rawMedias = await userMediaService.getMediasByPaths(pathsInSlice);

    const surviving = rawMedias.filter(
      (media) => !shouldFilterOutBlockedOrHiddenMediaUseCase.execute(media, currentUser),
    );

    // Re-order surviving medias to match `pathsInSlice` order — Flutter
    // saved_content_screen.dart:144-150.
    const ordered: UserMediaEntity[] = [];
    for (const path of pathsInSlice) {
      const match = surviving.find((media) => media.relativePath === path);
      if (match) {
        ordered.push(match);
      }
    }

    const hasMore = end < localItems.length;

    return { medias: ordered, hasMore };
  }
}

export const fetchSavedContentPageUseCase = new FetchSavedContentPageUseCase();
